/** Eksportene under «festival-program» i katalogen (docs/EKSPORT.md §9). API-halvdelen: `apps/api/src/export/definitions/festival-program/`. */
import type { ExportColumnMeta, ExportMeta } from '../types.js';

const L = 'export.festivalProgram.lineup.columns.';

/**
 * Lineupens kolonner. Nøklene er tabellens egne (`Table tableKey="lineup"`), så eksporten følger
 * kolonnene brukeren har valgt på skjermen; `day` finnes bare i eksporten.
 */
const LINEUP_COLUMNS: readonly ExportColumnMeta[] = [
  { key: 'order', labelKey: L + 'order', type: 'integer' },
  { key: 'artist', labelKey: L + 'artist', type: 'text' },
  { key: 'title', labelKey: L + 'title', type: 'text' },
  { key: 'stage', labelKey: L + 'stage', type: 'text' },
  { key: 'startsAt', labelKey: L + 'startsAt', type: 'datetime' },
  { key: 'endsAt', labelKey: L + 'endsAt', type: 'datetime' },
  { key: 'status', labelKey: L + 'status', type: 'status' },
  { key: 'day', labelKey: L + 'day', type: 'date', defaultHidden: true },
  { key: 'duration', labelKey: L + 'duration', type: 'integer', defaultHidden: true },
  { key: 'company', labelKey: L + 'company', type: 'text', defaultHidden: true },
  { key: 'notes', labelKey: L + 'notes', type: 'multiline', defaultHidden: true },
];

/**
 * LINEUP OG PROGRAM: tabellen på lineupen, med lineupens filter (status, scene, søk og
 * sortering), merkede rader og de kolonnene brukeren har valgt der. En gjest får det delingen
 * når, uten bookingstatus (239.31).
 */
const LINEUP: ExportMeta = {
  id: 'festival.lineup',
  domain: 'festival',
  kind: 'table',
  titleKey: 'export.festivalProgram.lineup.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['selection', 'filter', 'parent'],
  requires: ['performance:list'],
  guest: true,
  tableKey: 'lineup',
  columns: LINEUP_COLUMNS,
  params: ['editionId'],
  landscape: true,
};

/**
 * PLAKATLISTE: én del per festivaldag, en blokk per scene med tid og artist. Til oppslag på
 * backstage-veggen og i baren. Bare bekreftede poster (muntlig bekreftet og videre) med starttid.
 */
const POSTER: ExportMeta = {
  id: 'festival.poster',
  domain: 'festival',
  kind: 'document',
  titleKey: 'export.festivalProgram.poster.title',
  formats: ['pdf', 'docx'],
  scopes: ['parent', 'filter', 'selection'],
  requires: ['performance:list'],
  guest: true,
  params: ['editionId'],
};

/**
 * KJØREPLAN PER SCENE OG DAG: én del per scene og festivaldag, settene fra lineupen og scenetidene
 * fra hver programpost (get-in, linjesjekk, soundcheck …), med sceneskiftet mellom settene. Fra
 * lineupen (alle scener, eller scenen lineupen er filtrert på) og for merkede poster. Bare
 * bekreftede poster, som den delte kjøreplanen på lenkesiden.
 */
const RUNNING_ORDER: ExportMeta = {
  id: 'festival.running-order',
  domain: 'festival',
  kind: 'document',
  titleKey: 'export.festivalProgram.runningOrder.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object', 'parent', 'selection'],
  requires: ['performance:list'],
  guest: false,
  sections: [
    { key: 'sets', labelKey: 'export.festivalProgram.runningOrder.sections.sets' },
    { key: 'stageTimes', labelKey: 'export.festivalProgram.runningOrder.sections.stageTimes' },
    { key: 'changeovers', labelKey: 'export.festivalProgram.runningOrder.sections.changeovers' },
  ],
  params: ['editionId'],
};

/**
 * DAGSARK FOR PROGRAMPOSTEN: tidsplanen for posten (get-in, soundcheck, middag, sett, reise,
 * hotell) per dag, med kontaktene og hotellet. Ett ark per post; fra posten, merkede og alle.
 */
const DAY_SHEET: ExportMeta = {
  id: 'festival.day-sheet',
  domain: 'festival',
  kind: 'document',
  titleKey: 'export.festivalProgram.daySheet.title',
  formats: ['pdf', 'docx'],
  scopes: ['object', 'selection', 'parent'],
  requires: ['performance:read'],
  guest: true,
  sections: [
    { key: 'schedule', labelKey: 'export.festivalProgram.daySheet.sections.schedule' },
    { key: 'contacts', labelKey: 'export.festivalProgram.daySheet.sections.contacts' },
    { key: 'stay', labelKey: 'export.festivalProgram.daySheet.sections.stay' },
  ],
  params: ['editionId'],
};

const B = 'export.festivalProgram.bookingStatus.columns.';

/**
 * BOOKINGSTATUS FOR ÅRGANGEN: lineupen gruppert på bookingkjeden, fra ønskeliste til gjennomført
 * og avlyst til slutt, med antall per status. Kontraktstatus deles aldri med gjester (239.31).
 */
const BOOKING_STATUS: ExportMeta = {
  id: 'festival.booking-status',
  domain: 'festival',
  kind: 'table',
  titleKey: 'export.festivalProgram.bookingStatus.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['parent', 'filter', 'selection'],
  requires: ['performance:list'],
  guest: false,
  columns: [
    { key: 'status', labelKey: B + 'status', type: 'status' },
    { key: 'step', labelKey: B + 'step', type: 'text' },
    { key: 'artist', labelKey: B + 'artist', type: 'text' },
    { key: 'title', labelKey: B + 'title', type: 'text' },
    { key: 'stage', labelKey: B + 'stage', type: 'text' },
    { key: 'startsAt', labelKey: B + 'startsAt', type: 'datetime' },
    { key: 'company', labelKey: B + 'company', type: 'text', defaultHidden: true },
    { key: 'notes', labelKey: B + 'notes', type: 'multiline', defaultHidden: true },
  ],
  params: ['editionId'],
};

export const FESTIVAL_PROGRAM_EXPORTS: readonly ExportMeta[] = [
  LINEUP,
  POSTER,
  RUNNING_ORDER,
  DAY_SHEET,
  BOOKING_STATUS,
];
